import { createContext, useContext, useEffect, useState } from 'react';
import useClientActions from '../hooks/APIHooks/users/useClientActions';
import { Client, User } from '../types/users';
import { useAuthContext } from './AuthProvider';

// datatype of the context declared here
interface IClientContext {
  clientList: Array<Client>;
  client: Client | null;
  showDetailsModal: boolean;
  setClient: React.Dispatch<React.SetStateAction<Client | null>>;
  setShowDetailsModal: React.Dispatch<React.SetStateAction<boolean>>;
  isError: boolean;
  isLoading: boolean;
  isSuccess: boolean;
}

// creating the context here
const ClientContext = createContext<IClientContext | null>(null);

const ClientProvider = ({ children }: { children: React.ReactNode }) => {
  // integration of react hooks here
  const [clientList, setClientList] = useState<Array<Client>>([]);
  const [client, setClient] = useState<Client | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);

  // integration of context hooks here
  const { currentUser } = useAuthContext();

  // integration of API hooks here
  const { clientListHandler, clientListByAssociateIdHandler } =
    useClientActions(
      (currentUser as User)?.role_name === 'Associate'
        ? 'clientListById'
        : 'clientListHandler',
      (currentUser as User)?.role_name === 'Associate'
        ? (currentUser as User).id
        : undefined
    );

  // setting values into local state here
  useEffect(() => {
    if ((currentUser as User)?.role_name === 'Associate') {
      if (clientListByAssociateIdHandler.data?.is_success) {
        setClientList(
          clientListByAssociateIdHandler.data.data.map((client) => ({
            ...client,
            status: client?.is_active ? 'Active' : 'Inactive',
          }))
        );
      }
    } else {
      if (clientListHandler.data?.is_success) {
        setClientList(
          clientListHandler.data.data.map((client) => ({
            ...client,
            status: client?.is_active ? 'Active' : 'Inactive',
          }))
        );
      }
    }
  }, [
    currentUser,
    clientListByAssociateIdHandler.data,
    clientListHandler.data,
  ]);

  // rendering the context provider here
  return (
    <ClientContext.Provider
      value={{
        clientList,
        client,
        showDetailsModal,
        setClient,
        setShowDetailsModal,
        isError:
          (currentUser as User)?.role_name === 'Associate'
            ? clientListByAssociateIdHandler.isError
            : clientListHandler.isError,
        isLoading:
          (currentUser as User)?.role_name === 'Associate'
            ? clientListByAssociateIdHandler.isLoading
            : clientListHandler.isLoading,
        isSuccess:
          (currentUser as User)?.role_name === 'Associate'
            ? clientListByAssociateIdHandler.isSuccess
            : clientListHandler.isSuccess,
      }}
    >
      {children}
    </ClientContext.Provider>
  );
};

export default ClientProvider;

// custom hoo to access the client context declared here
export const useClientContext = () => {
  const context = useContext(ClientContext);

  if (!context) {
    throw new Error('useClientContext must be within ClientProvider!');
  }

  return context;
};
